// Date formatting, in one place.
//
// There were FIVE implementations of this across the card components: four
// byte-identical copies of `dateFormat()`, plus a fifth in SolvedCard called
// `dateFormatSolved()` that worked completely differently. So the Solved card
// rendered its two dates through two different algorithms -- and labelled both
// "(UTC+2)". See P1-14.
//
// Everything each of them got wrong:
//
//   * `(UTC+2)` was hardcoded while the value was formatted with local-time
//     getters. Correct only for a viewer who happens to be in UTC+2, and
//     silently wrong for anyone else.
//   * `getMonth() < 9 ? ...` -- reads as though comparing a 1-based month
//     against a 0-based one; right only by accident.
//   * getDate() and getHours() were never zero-padded, so the 5th at 09:05
//     rendered as `2026-7-5 9:5`.
//   * dateFormatSolved() string-split the raw ISO value instead of parsing it,
//     so it displayed UTC while the other formatter displayed local time. Two
//     dates on one card, two hours apart, both labelled the same.
//
// ----------------------------------------------------------------------------
// WHY CONVERTING IS SAFE HERE
//
// `Python_Files.upload_date` and `solved_date` are `@db.Timestamptz(6)`
// (prisma/schema.prisma), so Prisma hands Express a real Date and
// JSON.stringify emits an ISO string with an explicit `Z`. The instant is
// unambiguous, and the browser can be trusted to convert it.
//
// That property is load-bearing. A `timestamp without time zone` column would
// serialise to an ISO string whose `Z` is a lie -- the value would already be
// in the server's local zone -- and converting it again would shift every
// timestamp in the app by the server's offset. Hence the guard below: a value
// arriving WITHOUT zone information is not converted at all, and is rendered as
// the UTC it actually is.
//
// NOTE that with the "(UTC+2)" suffix removed, that fallback is no longer
// visible to the reader: an unconverted timestamp looks exactly like a
// converted one. It cannot arise while both columns stay `timestamptz`, which
// is the assumption to re-check if times ever look an hour or two out.
//
// This file is deliberately identical to the one in the sibling RIC BGO Tool.

/** Matches a trailing `Z`, `+02:00`, `-0530` etc. -- i.e. "this string names
 *  its own offset, so the instant it denotes is unambiguous". */
const HAS_TIMEZONE = /(?:Z|[+-]\d{2}:?\d{2})$/i;

const pad = (n) => String(n).padStart(2, "0");

/**
 * Renders an instant in the viewer's own timezone.
 *
 * @param {string|Date|null|undefined} value
 * @returns {string} e.g. "2026-07-30 15:51:12" in Johannesburg -- the same
 *   instant as "2026-07-30 13:51:12" seen from London. "—" when absent.
 */
export function formatDateTime(value) {
  const date = toDate(value);
  if (!date) return "—";

  // No offset in the source: do not guess one, show the UTC it already is.
  if (!carriesTimezone(value)) return utcStamp(date);

  return localStamp(date);
}

/**
 * The date alone, no time. Used where a time would be noise -- the admin
 * portal's "template uploaded on" line, which used to be a raw
 * `value.split("T")[0]` and so showed the UTC date even when the viewer's
 * local date had already rolled over.
 *
 * @param {string|Date|null|undefined} value
 * @returns {string} e.g. "2026-07-30", or "—" when absent.
 */
export function formatDate(value) {
  const date = toDate(value);
  if (!date) return "—";

  if (!carriesTimezone(value)) {
    return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
  }

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** null for anything unparseable, so callers have one thing to check. */
function toDate(value) {
  if (!value) return null;

  // A string with no zone is parsed as LOCAL time by the ES spec when it has a
  // time component, which would be a second silent shift on top of the first.
  // Pin it to UTC explicitly instead -- that is what it is being displayed as.
  const raw =
    typeof value === "string" && !HAS_TIMEZONE.test(value)
      ? `${value.trim().replace(" ", "T")}Z`
      : value;

  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
}

function carriesTimezone(value) {
  // A Date object is already an instant; only strings can be ambiguous.
  if (value instanceof Date) return true;
  return typeof value === "string" && HAS_TIMEZONE.test(value.trim());
}

function localStamp(date) {
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    ` ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
  );
}

function utcStamp(date) {
  // toISOString is always UTC and always zero-padded, which is the whole point.
  const iso = date.toISOString();
  return `${iso.slice(0, 10)} ${iso.slice(11, 19)}`;
}
